import React, { useState, useEffect } from "react";
import { Card, Row, Col, Form, Spinner, OverlayTrigger, Tooltip } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {  
  faEnvelope, 
  faSms, 
  faBell, 
  faClipboardList, 
  faPencilAlt, 
  faCheckCircle,
  faExclamationCircle, 
  faCalendarAlt, 
  faUsers, 
  faUmbrellaBeach, 
  faSuitcase,
  faUserSlash 
} from "@fortawesome/free-solid-svg-icons";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { toast } from "react-toastify";

const CHANNEL_META = {
  Email: { icon: faEnvelope, className: "channel-email" },
  Sms: { icon: faSms, className: "channel-sms" },
  WhatsApp: { icon: faWhatsapp, className: "channel-whatsapp" },
  InApp: { icon: faBell, className: "channel-inapp" },
};

const CHANNEL_ORDER = ["Email", "WhatsApp", "Sms", "InApp"];
const UNAVAILABLE_CHANNELS = ["WhatsApp", "Sms"];

const CHANNEL_PLACEHOLDER = {
  Email: "getEmailAlerts",       
  Sms: "getSmsAlerts",           
  WhatsApp: "getWhatsAppAlerts", 
  InApp: "getInAppAlerts",       
};

const DEFAULT_ENABLED_CHANNELS = ["Email", "InApp"];

const UNAVAILABLE_EVENT_CHANNELS = {
  WEEKLY_PLANNING_REMINDER: ["InApp"],
};

const EVENT_ICON_META = {
  TASK_ASSIGNED: { icon: faClipboardList, className: "bg-event-blue" },
  TASK_UPDATED: { icon: faPencilAlt, className: "bg-event-green" },
  TASK_UNASSIGNED: { icon: faUserSlash, className: "bg-event-red" },
  TASK_COMPLETED: { icon: faCheckCircle, className: "bg-event-teal" },
  TASK_OVERDUE: { icon: faExclamationCircle, className: "bg-event-orange" },
  WEEKLY_PLANNING_REMINDER: { icon: faCalendarAlt, className: "bg-event-blue" },
  LEAVE_REQUESTED: { icon: faSuitcase, className: "bg-event-orange" },
  VACATION_DECISION: { icon: faUmbrellaBeach, className: "bg-event-purple" },
};
const DEFAULT_EVENT_ICON = { icon: faBell, className: "bg-event-blue" };

const NotificationSettings = () => {
  const { t } = useTranslation();
  const customerId = localStorage.getItem("customerId");
  const [isLoading, setIsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [channels, setChannels] = useState([]);     
  const [categories, setCategories] = useState([]);    

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/notificationSettingsRoute/getSettings/${customerId}`,
          { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
        );
        setChannels(res.data.channels || []);
        setCategories(res.data.categories || []);
      } catch (err) {
        console.error("Failed to fetch notification settings", err);
        toast.error(t("fetchFailed"), { position: "top-right", autoClose: 3000, toastId: "notification-settings-fetch-error" });
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [customerId]);

  const toggleChannel = async (channelKey, enabled) => {
    setChannels(prev => prev.map(c => c.Channel === channelKey ? { ...c, Enabled: enabled } : c));

    setCategories(prev => prev.map(cat => ({
      ...cat,
      Events: cat.Events.map(ev => ({
        ...ev,
        Prefs: { ...ev.Prefs, [channelKey]: enabled }
      }))
    })));

    try {
      await axios.patch(
        `${process.env.REACT_APP_API_URL}/notificationSettingsRoute/channel/toggle`,
        { CustomerId: customerId, Channel: channelKey, Enabled: enabled },
        { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
      );
      if (channelKey === "InApp") {
        window.dispatchEvent(new CustomEvent("inAppChannelToggled", { detail: { enabled } }));
      }
    } catch (err) {
      console.error("Channel toggle failed", err);
      setChannels(prev => prev.map(c => c.Channel === channelKey ? { ...c, Enabled: !enabled } : c));
      setCategories(prev => prev.map(cat => ({
        ...cat,
        Events: cat.Events.map(ev => ({
          ...ev,
          Prefs: { ...ev.Prefs, [channelKey]: !enabled }
        }))
      })));
      if (channelKey === "InApp") {
        window.dispatchEvent(new CustomEvent("inAppChannelToggled", { detail: { enabled: !enabled } }));
      }
      toast.error(t("updateFailed"), { position: "top-right", autoClose: 3000 });
    }
  };

  const togglePreference = async (categoryKey, eventKey, channelKey, enabled) => {
    setCategories(prev => prev.map(cat => cat.CategoryKey !== categoryKey ? cat : {
      ...cat,
      Events: cat.Events.map(ev => ev.EventKey !== eventKey ? ev : {
        ...ev, Prefs: { ...ev.Prefs, [channelKey]: enabled }
      })
    }));

    try {
      await axios.patch(
        `${process.env.REACT_APP_API_URL}/notificationSettingsRoute/preference/toggle`,
        { CustomerId: customerId, EventKey: eventKey, Channel: channelKey, Enabled: enabled },
        { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
      );
    } catch (err) {
      console.error("Preference toggle failed", err);
      // rollback
      setCategories(prev => prev.map(cat => cat.CategoryKey !== categoryKey ? cat : {
        ...cat,
        Events: cat.Events.map(ev => ev.EventKey !== eventKey ? ev : {
          ...ev, Prefs: { ...ev.Prefs, [channelKey]: !enabled }
        })
      }));
      toast.error(t("updateFailed"), { position: "top-right", autoClose: 3000 });
    }
  };

  if (isLoading) {
    return (
      <div className="text-center my-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">{t("loading")}</span>
        </Spinner>
      </div>
    );
  }

  return (
    <>
      {/* Delivery Channels */}
      <Card className="mb-3">
        <Card.Body>
          <h6 className="fw-semibold mb-1">{t("deliveryChannels")}</h6>
          <small className="text-muted d-block mb-3">{t("chooseHowToReceiveNotifications")}</small>
          <Row className="g-3">
            {CHANNEL_ORDER.map(channelKey => {
              const meta = CHANNEL_META[channelKey];
              const channel = channels.find(c => c.Channel === channelKey) || { 
                Enabled: DEFAULT_ENABLED_CHANNELS.includes(channelKey), 
                Destination: "" 
              };
              return (
                <Col lg={3} sm={6} xs={6} key={channelKey}>
                  <Card className="h-100 border shadow-none">
                    <Card.Body className="channel-card-body">
                      <div className="channel-card-top">
                        <div className="channel-left">
                          <div className={`channel-icon-circle icon-circle-40 rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 ${meta.className}`}>
                            <FontAwesomeIcon icon={meta.icon} />
                          </div>
                          <div className="channel-info">
                            <h6 className="fw-semibold mb-0">{t(`channel_${channelKey}`)}</h6>
                              <small className="text-muted d-block text-truncate mt-1">
                                {channel.Destination || t(CHANNEL_PLACEHOLDER[channelKey])}
                              </small>
                          </div>
                        </div>
                        {UNAVAILABLE_CHANNELS.includes(channelKey) ? (
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>{t("channelComingSoon")}</Tooltip>}
                          >
                            <span>
                              <Form.Check
                                type="switch"
                                id={`channel-${channelKey}`}
                                className="notif-switch"
                                checked={false}
                                disabled
                                readOnly
                              />
                            </span>
                          </OverlayTrigger>
                          ) : (
                          <Form.Check
                            type="switch"
                            id={`channel-${channelKey}`}
                            className="notif-switch"
                            checked={!!channel.Enabled}
                            onChange={(e) => toggleChannel(channelKey, e.target.checked)}
                          />
                        )}
                      </div>

                      <div className="channel-pill-row mt-2">
                        <span className={`channel-status-pill ${channel.Enabled ? "active" : "inactive"}`}>
                          {channel.Enabled ? t("active") : t("inactive")}
                        </span>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              );
            })}
          </Row>
        </Card.Body>
      </Card>

      {/* My Notification Preferences */}
      <Card className="mb-3 overflow-hidden notif-pref-card">
        <Card.Body>
          <Row className="align-items-end mb-2">
            <Col lg={6}>
              <h6 className="fw-semibold mb-1">{t("notificationPreferences")}</h6>
              <small className="text-muted d-block text-truncate">{t("chooseWhichUpdatesToReceive")}</small>
            </Col>
            <Col lg={6} className="d-none d-lg-block">
              <Row>
                {CHANNEL_ORDER.map(ch => (
                  <Col key={ch} className="text-center">
                    <div className="d-flex align-items-center justify-content-center gap-1">
                      <FontAwesomeIcon icon={CHANNEL_META[ch].icon} className={`font-size-12 channel-header-icon ${CHANNEL_META[ch].className}`} />
                      <h6 className={`fw-semibold fs-12 mb-0 ${CHANNEL_META[ch].className}`}>{t(`channel_${ch}_short`)}</h6>
                    </div>
                  </Col>
                ))}
              </Row>
            </Col>
          </Row>

          {categories.map(cat => (
            <div key={cat.CategoryKey} className="category-block">
              <div className="category-header font-size-12 fw-semibold mb-1">       
                {t(cat.CategoryLabel)} 
              </div>
              {cat.Events.map(ev => (
                <Row key={ev.EventKey} className="preference-row align-items-center py-3 border-bottom">
                  <Col lg={6} className="d-flex align-items-center mb-3 mb-lg-0">
                    <div className={`event-row-icon ${(EVENT_ICON_META[ev.EventKey] || DEFAULT_EVENT_ICON).className}`}>
                      <FontAwesomeIcon icon={(EVENT_ICON_META[ev.EventKey] || DEFAULT_EVENT_ICON).icon} />
                    </div>
                    <div>
                      <h6 className="mb-0">{t(ev.EventLabel)}</h6>
                      {ev.EventDescription && <small className="text-muted d-block mt-1">{t(ev.EventDescription)}</small>}
                    </div>
                  </Col>
                  <Col lg={6}>
                    <Row>
                      {CHANNEL_ORDER.map(channelKey => {
                        const channelEnabledGlobally = (channels.find(c => c.Channel === channelKey) || {}).Enabled;
                        const isChannelUnavailable = UNAVAILABLE_CHANNELS.includes(channelKey);
                        const isEventChannelUnavailable = (UNAVAILABLE_EVENT_CHANNELS[ev.EventKey] || []).includes(channelKey);
                        const isDisabled = isChannelUnavailable || isEventChannelUnavailable || !channelEnabledGlobally;

                        const switchElement = (
                          <Form.Check
                            type="switch"
                            id={`${ev.EventKey}-${channelKey}`}
                            className="notif-switch"
                            checked={isChannelUnavailable || isEventChannelUnavailable ? false : !!ev.Prefs[channelKey]}
                            disabled={isDisabled}
                            readOnly={isChannelUnavailable || isEventChannelUnavailable}
                            onChange={(e) => {
                              if (isChannelUnavailable || isEventChannelUnavailable) return;
                              togglePreference(cat.CategoryKey, ev.EventKey, channelKey, e.target.checked);
                            }}
                          />
                        );

                        return (
                          <Col key={channelKey} xs={3} className="text-center">
                            <div className="d-lg-none d-flex align-items-center justify-content-center gap-1 mb-1">
                              <FontAwesomeIcon icon={CHANNEL_META[channelKey].icon} className={`channel-toggle-icon ${CHANNEL_META[channelKey].className}`} />
                              <small className={`channel-toggle-label fw-semibold ${CHANNEL_META[channelKey].className}`}>
                                {t(`channel_${channelKey}_short`)}
                              </small>
                            </div>
                            {(isChannelUnavailable || isEventChannelUnavailable) ? (
                              <OverlayTrigger
                                placement="top"
                                overlay={<Tooltip>{t(isEventChannelUnavailable ? "eventChannelComingSoon" : "channelComingSoon")}</Tooltip>}
                              >
                                <span>{switchElement}</span>
                              </OverlayTrigger>
                            ) : (
                              switchElement
                            )}
                          </Col>
                        );
                      })}
                    </Row>
                  </Col>
                </Row>
              ))}
            </div>
          ))}
        </Card.Body>
      </Card>
    </>
  );
};

export default NotificationSettings;